import {
  OutboxEventStatus,
  enumOutboxEventStatus,
} from '@rematter/frontend-lib-api-client';
import { Col, Row, Badge, humanReadableDateTime } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { AssetEventDetail } from './asset';
import { DriverCertificationEventDetail, DriverEventDetail } from './driver';
import { RouteEventDetail } from './route';
import { TrailerEventDetail } from './trailer';
import { TruckEventDetail } from './truck';

import {
  $remoteTransportationOutboxEvents,
  useSelectedTransportationEventId,
} from '@/audit-log/_state/transportationEvents.state';
import { useTranslations } from '@/audit-log/i18n/useTranslations';
import { getLocale } from '~/shared/utils/locale';

const getStatusColor = (status: OutboxEventStatus) => {
  switch (status) {
    case enumOutboxEventStatus.Processed:
      return 'success';
    case enumOutboxEventStatus.Pending:
      return 'warning';
    case enumOutboxEventStatus.Failed:
      return 'failure';
    case enumOutboxEventStatus.DeadLetter:
      return 'failure';
    default:
      return 'neutral';
  }
};

type MetadataRowProps = { label: string; value: string };

const MetadataRow = ({ label, value }: MetadataRowProps) => (
  <Row className="@MetadataRow justify-between py-1 text-sm">
    <span className="text-neutral/60">{label}</span>
    <span className="font-mono text-xs">{value}</span>
  </Row>
);

type AggregateType =
  | 'Asset'
  | 'Driver'
  | 'DriverCertification'
  | 'Truck'
  | 'Trailer'
  | 'Route'
  | 'Service'
  | 'Task'
  | 'ContainerType'
  | 'TransportationType'
  | 'FreightForwarder'
  | 'FreightLine'
  | 'ShippingCarrier';

const getAggregateTypeFromEventName = (
  eventName: string,
): AggregateType | null => {
  if (eventName.startsWith('Asset')) return 'Asset';
  if (eventName.startsWith('DriverCertification')) return 'DriverCertification';
  if (eventName.startsWith('Driver')) return 'Driver';
  if (eventName.startsWith('Truck')) return 'Truck';
  if (eventName.startsWith('Trailer')) return 'Trailer';
  if (eventName.startsWith('Route')) return 'Route';
  if (eventName.startsWith('Service')) return 'Service';
  if (eventName.startsWith('Task')) return 'Task';
  if (eventName.startsWith('ContainerType')) return 'ContainerType';
  if (eventName.startsWith('TransportationType')) return 'TransportationType';
  if (eventName.startsWith('FreightForwarder')) return 'FreightForwarder';
  if (eventName.startsWith('FreightLine')) return 'FreightLine';
  if (eventName.startsWith('ShippingCarrier')) return 'ShippingCarrier';
  return null;
};

type EventPayloadDetailProps = {
  aggregateType: AggregateType;
  payload: string;
};

const JsonFallbackView = ({ payload }: { payload: string }) => {
  try {
    const parsed = JSON.parse(payload);
    return (
      <Col gap1 className="flex-1 rounded-md border border-neutral/10 bg-white">
        <span className="border-b border-neutral/10 p-3 text-sm font-medium text-neutral/80">
          Payload
        </span>
        <pre className="flex-1 overflow-auto p-3 font-mono text-xs">
          {JSON.stringify(parsed, null, 2)}
        </pre>
      </Col>
    );
  } catch {
    return (
      <Col gap1 className="flex-1 rounded-md border border-neutral/10 bg-white">
        <span className="border-b border-neutral/10 p-3 text-sm font-medium text-neutral/80">
          Payload
        </span>
        <pre className="flex-1 overflow-auto p-3 font-mono text-xs">
          {payload}
        </pre>
      </Col>
    );
  }
};

const EventPayloadDetail = ({
  aggregateType,
  payload,
}: EventPayloadDetailProps) => {
  switch (aggregateType) {
    case 'Asset':
      return <AssetEventDetail payload={payload} />;
    case 'Driver':
      return <DriverEventDetail payload={payload} />;
    case 'DriverCertification':
      return <DriverCertificationEventDetail payload={payload} />;
    case 'Truck':
      return <TruckEventDetail payload={payload} />;
    case 'Trailer':
      return <TrailerEventDetail payload={payload} />;
    case 'Route':
      return <RouteEventDetail payload={payload} />;
    case 'Service':
    case 'Task':
    case 'ContainerType':
    case 'TransportationType':
    case 'FreightForwarder':
    case 'FreightLine':
    case 'ShippingCarrier':
      return <JsonFallbackView payload={payload} />;
  }
};

export const TransportationEventDetail = () => {
  const t = useTranslations();
  const [selectedEventId] = useSelectedTransportationEventId();

  const { data } = useAtomValue($remoteTransportationOutboxEvents);

  const selectedEvent = data?.events.find((e) => e.id === selectedEventId);

  if (!selectedEvent) {
    return (
      <Col className="@TransportationEventDetail flex-1 items-center justify-center bg-neutral/2 text-neutral/40">
        <span>{t.transportationEventsView.detail.empty}</span>
      </Col>
    );
  }

  const aggregateType = getAggregateTypeFromEventName(selectedEvent.eventName);

  return (
    <Col className="@TransportationEventDetail flex-1 overflow-hidden bg-neutral/2">
      <Col className="flex-1 overflow-y-auto p-4" gap4>
        <Col gap2>
          <Row className="items-center justify-between">
            <span className="text-lg font-medium">
              {selectedEvent.eventName}
            </span>
            <Badge
              color={getStatusColor(selectedEvent.status as OutboxEventStatus)}
            >
              {selectedEvent.status}
            </Badge>
          </Row>
        </Col>

        <Col gap1 className="rounded-md border border-neutral/10 bg-white p-3">
          <span className="mb-2 text-sm font-medium text-neutral/80">
            {t.transportationEventsView.detail.metadata.title}
          </span>
          <MetadataRow
            label={t.transportationEventsView.detail.metadata.eventId}
            value={selectedEvent.id}
          />
          <MetadataRow
            label={t.transportationEventsView.detail.metadata.aggregateId}
            value={
              selectedEvent.metadata.aggregateId ??
              t.transportationEventsView.detail.notAvailable
            }
          />
          <MetadataRow
            label={t.transportationEventsView.detail.metadata.topic}
            value={selectedEvent.topic}
          />
          <MetadataRow
            label={t.transportationEventsView.detail.metadata.version}
            value={String(selectedEvent.version)}
          />
          <MetadataRow
            label={t.transportationEventsView.detail.metadata.retryCount}
            value={String(selectedEvent.retryCount)}
          />
          <MetadataRow
            label={t.transportationEventsView.detail.metadata.createdAt}
            value={humanReadableDateTime(selectedEvent.timestamps.createdAt, {
              locale: getLocale(),
              includeYear: true,
              includeSeconds: true,
            })}
          />
          <MetadataRow
            label={t.transportationEventsView.detail.metadata.createdBy}
            value={
              selectedEvent.createdByUser
                ? `${(selectedEvent.createdByUser as { firstName?: string; lastName?: string }).firstName ?? ''} ${(selectedEvent.createdByUser as { firstName?: string; lastName?: string }).lastName ?? ''}`.trim() ||
                  t.transportationEventsView.detail.notAvailable
                : t.transportationEventsView.detail.notAvailable
            }
          />
          {selectedEvent.lastRetry && (
            <MetadataRow
              label={t.transportationEventsView.detail.metadata.lastRetry}
              value={humanReadableDateTime(selectedEvent.lastRetry, {
                locale: getLocale(),
                includeYear: true,
                includeSeconds: true,
              })}
            />
          )}
        </Col>

        {aggregateType ? (
          <EventPayloadDetail
            aggregateType={aggregateType}
            payload={selectedEvent.payload as string}
          />
        ) : (
          <JsonFallbackView payload={selectedEvent.payload as string} />
        )}
      </Col>
    </Col>
  );
};
